import React, { useState } from "react";
import BottomSheet from "src/components/app/BottomSheet";
import ReminderFormContent, {
  ReminderType,
} from "src/components/app/forms/ReminderFormContent";
import Button from "src/components/app/Button";
import { useAppSelector } from "src/redux/hooks";
import {
  getExistingReminderByLogKey,
  ReminderData,
} from "src/helpers/reminderHelpers";

interface SetRemindetBottomSheetProps {
  show: boolean;
  handleClose: () => void;
  /** Log key e.g. "water-logs", "sleep-logs" */
  type: string;
}

const typeLabel: Record<string, string> = {
  "water-logs": "Water",
  "sleep-logs": "Sleep",
  "steps-logs": "Steps",
  "fatigue-logs": "Fatigue",
  "diet-logs": "Diet",
  "medication-logs": "Medication",
  "exercise-logs": "Exercise",
};

const logKeyToReminderType: Record<string, ReminderType> = {
  "water-logs": "WATER",
  "sleep-logs": "SLEEP",
  "steps-logs": "STEPS",
  "fatigue-logs": "FATIGUE",
  "diet-logs": "DIET",
  "medication-logs": "MEDICATION",
  "exercise-logs": "EXERCISE",
};

const SetRemindetBottomSheet: React.FC<SetRemindetBottomSheetProps> = ({
  show,
  handleClose,
  type,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [existingReminder, setExistingReminder] = useState<ReminderData | null>(
    null
  );

  const { reminder } = useAppSelector((state) => state.reminder);
  const existingReminderForType = getExistingReminderByLogKey(
    reminder?.data?.reminders,
    type
  );

  const reminderType = logKeyToReminderType[type];
  const label = typeLabel[type] ?? type;

  // When sheet opens with existing reminder, show "Update?" first; otherwise show form
  const showUpdatePrompt =
    show && !!existingReminderForType && !showForm && !existingReminder;
  const showFormContent = show && (showForm || !existingReminderForType);

  const handleUpdateConfirm = () => {
    setExistingReminder(existingReminderForType ?? null);
    setShowForm(true);
  };

  const handleUpdateCancel = () => {
    handleClose();
  };

  const handleFormClose = () => {
    setShowForm(false);
    setExistingReminder(null);
    handleClose();
  };

  if (!reminderType) {
    return (
      <BottomSheet
        show={show}
        handleClose={handleClose}
        title="Set Reminder"
        showCloseButton
      >
        <div className="p-4 text-sm text-gray-500">
          Reminders are not available for this type.
        </div>
      </BottomSheet>
    );
  }

  const title = existingReminder ? "Update Reminder" : `Set ${label} Reminder`;

  return (
    <BottomSheet
      show={show}
      handleClose={handleClose}
      title={title}
      showCloseButton
    >
      <div className="p-4">
        {showUpdatePrompt && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              You already have a reminder for {label}. Do you want to update it?
            </p>
            <div className="flex gap-2">
              <Button
                variant="outlined"
                rounded="full"
                fullWidth
                onClick={handleUpdateCancel}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                rounded="full"
                fullWidth
                onClick={handleUpdateConfirm}
              >
                Update Reminder
              </Button>
            </div>
          </div>
        )}

        {showFormContent && (
          <ReminderFormContent
            reminderType={reminderType}
            existingReminder={existingReminder ?? null}
            onClose={handleFormClose}
            visible={show}
          />
        )}
      </div>
    </BottomSheet>
  );
};

export default SetRemindetBottomSheet;
